import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faDroplet, faPenToSquare, faTableList } from '@fortawesome/free-solid-svg-icons';
import { Tabs, TabDefinition } from '../../common/Tabs';
import AppModal from '../../common/AppModal';
import { usePhotoLoader } from './usePhotoLoader';
import VarPhotoGrid from './VarPhotoGrid';
import { getBackendUrl } from '../../../services/configService';
import { usePermissions } from '../../../context/PermissionContext';
import type { VAREntity } from '../../../types/var';
import './VARPopupContent.scss';

interface PopupState {
  popupElement: HTMLElement;
  entity: VAREntity;
}

const VARPopupContent: React.FC = () => {
  const [popup, setPopup] = useState<PopupState | null>(null);
  const [expanded, setExpanded] = useState(false);
  const { canDo } = usePermissions();
  const canEdit = canDo('var', 'update');
  const canFlagRed = canDo('var', 'flag_red');
  const canFlagSprinkler = canDo('var', 'flag_sprinkler');

  const { entity: loadedDetails, photos, loading: loadingDetails, error: detailsError, refresh } =
    usePhotoLoader(expanded && popup ? popup.entity.var_id : null, expanded);

  // Prefer freshly-loaded entity (from usePhotoLoader) over the stale entity passed at popup open
  const details = loadedDetails || (popup ? popup.entity : null);

  // Refetch just the entity (no CMS images) — used when expanded view isn't
  // active but data underneath the popup changed (flag toggle, edit save).
  const refetchEntity = useCallback(async (varId: string) => {
    try {
      const res = await fetch(`${getBackendUrl() || ''}/api/var/by-var-id/${encodeURIComponent(varId)}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.data) {
        setPopup((prev) => prev ? { ...prev, entity: data.data } : null);
      }
    } catch (err) {
      console.warn('[VARPopupContent] refetchEntity failed', err);
    }
  }, []);

  // Listen for popup open/close from VARLayer
  useEffect(() => {
    const handleOpen = (e: Event) => {
      const { popupElement, entity } = (e as CustomEvent).detail as PopupState;
      setPopup({ popupElement, entity });
      setExpanded(false);
    };
    const handleClose = () => {
      setPopup(null);
      setExpanded(false);
    };
    window.addEventListener('varPopup:open', handleOpen);
    window.addEventListener('varPopup:close', handleClose);
    return () => {
      window.removeEventListener('varPopup:open', handleOpen);
      window.removeEventListener('varPopup:close', handleClose);
    };
  }, []);

  // Refresh popup when underlying VAR data changes (flag toggle, edit save, delete)
  useEffect(() => {
    if (!popup) return;
    const currentVarId = popup.entity.var_id;

    const handleChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ varId?: string; deleted?: boolean }>).detail;
      // Only react to changes for the currently-open VAR (or events with no varId — apply to all)
      if (detail?.varId && detail.varId !== currentVarId) return;

      if (detail?.deleted) {
        // VAR was deleted — close the popup
        window.dispatchEvent(new CustomEvent('varPopup:close'));
        return;
      }
      refetchEntity(currentVarId);
      if (expanded) refresh();
    };

    window.addEventListener('varDataChanged', handleChanged);
    window.addEventListener('varFlagToggled', handleChanged);
    return () => {
      window.removeEventListener('varDataChanged', handleChanged);
      window.removeEventListener('varFlagToggled', handleChanged);
    };
  }, [popup, expanded, refetchEntity, refresh]);

  const handleMore = useCallback(() => setExpanded(true), []);
  const handleLess = useCallback(() => setExpanded(false), []);

  const handleViewInExplorer = useCallback(() => {
    if (!popup) return;
    window.dispatchEvent(new CustomEvent('openVarExplorer', { detail: { varId: popup.entity.var_id } }));
  }, [popup]);

  const handleEdit = useCallback(() => {
    if (!popup) return;
    window.dispatchEvent(new CustomEvent('varEdit:open', { detail: { varId: popup.entity.var_id } }));
  }, [popup]);

  const handleFlagToggle = useCallback((flagType: 'redFlag' | 'sprinkler') => {
    if (!popup) return;
    window.dispatchEvent(new CustomEvent('varFlagToggle', {
      detail: { entity: popup.entity, flagType },
    }));
  }, [popup]);

  if (!popup) return null;

  const { entity } = popup;
  const redFlagActive = entity.red_flag_active === true;
  const sprinklerActive = entity.sprinklerFlag?.active === true;

  const accessList = details ? [
    details.access?.Road && 'Road',
    details.access?.AircraftFW && 'Fixed-wing',
    details.access?.AircraftRW && 'Rotary',
    details.access?.BoatRiver && 'Boat/River',
  ].filter(Boolean) as string[] : [];

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  const infoTab = (
    <div className="var-popup-info-tab">
      {loadingDetails && <p className="var-popup-ext__loading">Loading details...</p>}
      {detailsError && <p className="var-popup-ext__error">{detailsError}</p>}
      {!loadingDetails && details && (
        <>
          <div className="var-modal__sub-row">
            <div className="var-modal__sub-cell">
              <span className="var-modal__sub-label">Subtype</span>
              <span className="var-modal__sub-value">{details.var_subtype || 'N/A'}</span>
            </div>
            <div className="var-modal__sub-cell">
              <span className="var-modal__sub-label">Occupancy</span>
              <span className="var-modal__sub-value">{details.occupancy || 'N/A'}</span>
            </div>
            <div className="var-modal__sub-cell">
              <span className="var-modal__sub-label">Region</span>
              <span className="var-modal__sub-value">{details.region_name || 'N/A'}</span>
            </div>
          </div>

          {details.details && (
            <div className="var-popup-ext__section var-popup-ext__section--full">
              <h5>Details</h5>
              <p>{details.details}</p>
            </div>
          )}

          <div className="var-modal__sections-grid">
            {details.location?.coordinates && (
              <div className="var-popup-ext__section">
                <h5>Location</h5>
                <p>
                  {details.location.coordinates[1].toFixed(5)}, {details.location.coordinates[0].toFixed(5)}
                </p>
              </div>
            )}

            {details.keywords && details.keywords.length > 0 && (
              <div className="var-popup-ext__section">
                <h5>Keywords</h5>
                <div className="var-popup-ext__chips">
                  {details.keywords.map((k, i) => (
                    <span key={i} className="var-popup-ext__chip">{k}</span>
                  ))}
                </div>
              </div>
            )}

            {accessList.length > 0 && (
              <div className="var-popup-ext__section">
                <h5>Access</h5>
                <div className="var-popup-ext__chips">
                  {accessList.map((a) => (
                    <span key={a} className="var-popup-ext__chip">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {(details.verification?.Date || details.verification?.Method || details.verification?.VerifiedBy || details.verification?.Notes) && (
              <div className="var-popup-ext__section">
                <h5>Verification</h5>
                {details.verification.Date && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Date:</span>
                    <span className="var-popup__value">{formatDate(details.verification.Date)}</span>
                  </div>
                )}
                {details.verification.Method && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Method:</span>
                    <span className="var-popup__value">{details.verification.Method}</span>
                  </div>
                )}
                {details.verification.VerifiedBy && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">By:</span>
                    <span className="var-popup__value">{details.verification.VerifiedBy}</span>
                  </div>
                )}
                {details.verification.Notes && <p>{details.verification.Notes}</p>}
              </div>
            )}

            {(details.contact?.name || details.contact?.phone || details.contact?.email) && (
              <div className="var-popup-ext__section">
                <h5>Contact</h5>
                {details.contact.name && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Name:</span>
                    <span className="var-popup__value">{details.contact.name}</span>
                  </div>
                )}
                {details.contact.phone && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Phone:</span>
                    <span className="var-popup__value">{details.contact.phone}</span>
                  </div>
                )}
                {details.contact.email && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Email:</span>
                    <span className="var-popup__value">{details.contact.email}</span>
                  </div>
                )}
              </div>
            )}

            {details.red_flag_active && (
              <div className="var-popup-ext__section var-popup-ext__section--red">
                <h5>Red Flag</h5>
                {details.red_flag_status && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Status:</span>
                    <span className="var-popup__value">{details.red_flag_status}</span>
                  </div>
                )}
                {details.red_flag_date && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Date:</span>
                    <span className="var-popup__value">{formatDate(details.red_flag_date)}</span>
                  </div>
                )}
                {details.red_flag_created_by && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">By:</span>
                    <span className="var-popup__value">{details.red_flag_created_by}</span>
                  </div>
                )}
                {details.red_flag_reason && <p>{details.red_flag_reason}</p>}
              </div>
            )}

            {details.sprinklerFlag?.active && (
              <div className="var-popup-ext__section var-popup-ext__section--sprinkler">
                <h5>Sprinkler</h5>
                {details.sprinklerFlag.name && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Name:</span>
                    <span className="var-popup__value">{details.sprinklerFlag.name}</span>
                  </div>
                )}
                {details.sprinklerFlag.sprinkerlocaldate && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">Date:</span>
                    <span className="var-popup__value">{formatDate(details.sprinklerFlag.sprinkerlocaldate)}</span>
                  </div>
                )}
                {details.sprinklerFlag.created_by && (
                  <div className="var-popup-ext__row">
                    <span className="var-popup__label">By:</span>
                    <span className="var-popup__value">{details.sprinklerFlag.created_by}</span>
                  </div>
                )}
                {details.sprinklerFlag.reason && <p>{details.sprinklerFlag.reason}</p>}
              </div>
            )}
          </div>

          <div className="var-popup-ext__section var-popup-ext__section--meta">
            <div className="var-popup-ext__grid">
              {details.created_by && (
                <div className="var-popup-ext__row">
                  <span className="var-popup__label">Created:</span>
                  <span className="var-popup__value">
                    {details.created_by}{details.created_at ? ` · ${formatDate(details.created_at)}` : ''}
                  </span>
                </div>
              )}
              {details.updated_by && (
                <div className="var-popup-ext__row">
                  <span className="var-popup__label">Updated:</span>
                  <span className="var-popup__value">
                    {details.updated_by}{details.updated_at ? ` · ${formatDate(details.updated_at)}` : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const photosTab = (
    <div className="var-popup-photos-tab">
      <VarPhotoGrid photos={photos} emptyMessage="No photos" />
    </div>
  );

  const tabs: TabDefinition[] = [{ id: 'info', label: 'Info', content: infoTab }];
  if (photos.length > 0) tabs.push({ id: 'photos', label: `Photos (${photos.length})`, content: photosTab });

  const flagButtons = (
    <>
      {canFlagRed && (
        <button
          type="button"
          className={`var-popup__icon-btn var-popup__icon-btn--red${redFlagActive ? ' var-popup__icon-btn--active' : ''}`}
          onClick={() => handleFlagToggle('redFlag')}
          title={redFlagActive ? 'Deactivate Red Flag' : 'Activate Red Flag'}
        >
          <FontAwesomeIcon icon={faFlag} /> Red Flag
        </button>
      )}
      {canFlagSprinkler && (
        <button
          type="button"
          className={`var-popup__icon-btn var-popup__icon-btn--sprinkler${sprinklerActive ? ' var-popup__icon-btn--active' : ''}`}
          onClick={() => handleFlagToggle('sprinkler')}
          title={sprinklerActive ? 'Deactivate Sprinkler' : 'Activate Sprinkler'}
        >
          <FontAwesomeIcon icon={faDroplet} /> Sprinkler
        </button>
      )}
      <button
        type="button"
        className="var-popup__icon-btn"
        onClick={handleViewInExplorer}
        title="View in Explorer"
      >
        <FontAwesomeIcon icon={faTableList} /> Explorer
      </button>
    </>
  );

  const compactContent = (
    <div className="var-popup">
      <h4 className="var-popup__title var-popup__title--compact">{entity.name || 'Unknown'}</h4>

      <div className="var-popup__row">
        <span className="var-popup__label">Type:</span>
        <span className="var-popup__value">{entity.var_type || 'N/A'}</span>
      </div>
      <div className="var-popup__row">
        <span className="var-popup__label">Status:</span>
        <span className="var-popup__value">{entity.status || 'N/A'}</span>
      </div>
      <div className="var-popup__row">
        <span className="var-popup__label">VAR ID:</span>
        <span className="var-popup__value">{entity.var_id || 'N/A'}</span>
      </div>

      <div className="var-popup__compact-actions">{flagButtons}</div>

      <div className="var-popup__actions">
        <div className="var-popup__actions-row">
          {canEdit && (
            <button type="button" className="var-popup__btn var-popup__btn--secondary" onClick={handleEdit}>
              <FontAwesomeIcon icon={faPenToSquare} /> Edit
            </button>
          )}
          <button type="button" className="var-popup__btn var-popup__btn--primary" onClick={handleMore}>
            More
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(compactContent, popup.popupElement)}

      <AppModal
        isOpen={expanded}
        onClose={handleLess}
        width={800}
        maxHeight="90vh"
        minHeight="80vh"
        headerAccent
        header={
          <div className="var-modal__header-row">
            <span className="var-modal__title">{entity.name || 'Unknown'}</span>
            <div className="var-modal__header-actions">{flagButtons}</div>
          </div>
        }
      >
        <div className="var-modal">
          <div className="var-modal__key-row">
            <div className="var-modal__key-cell">
              <span className="var-modal__key-label">Type</span>
              <span className="var-modal__key-value">{entity.var_type || 'N/A'}</span>
            </div>
            <div className="var-modal__key-cell">
              <span className="var-modal__key-label">Status</span>
              <span className="var-modal__key-value">{entity.status || 'N/A'}</span>
            </div>
            <div className="var-modal__key-cell">
              <span className="var-modal__key-label">VAR ID</span>
              <span className="var-modal__key-value">{entity.var_id || 'N/A'}</span>
            </div>
          </div>

          <div className="var-modal__body">
            <Tabs tabs={tabs} />
          </div>

          <div className="var-modal__footer">
            {canEdit && (
              <button type="button" className="var-popup__btn var-popup__btn--secondary" onClick={handleEdit}>
                <FontAwesomeIcon icon={faPenToSquare} /> Edit
              </button>
            )}
          </div>
        </div>
      </AppModal>
    </>
  );
};

export default VARPopupContent;
